/**
 * MotivoSection Component - Motivo/Responsável management section
 */
import React from 'react';
import { Plus, Pencil, Trash2, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import PermissionGate from '../PermissionGate';

interface Motivo {
  id: number;
  motivo: string;
  responsavel: string;
}

interface NewMotivo {
  motivo: string;
  responsavel: string;
}

interface MotivoSectionProps {
  motivos: Motivo[];
  isLoadingMotivos: boolean;
  isSavingMotivo: boolean;
  editingMotivo: number | null;
  newMotivo: NewMotivo;
  editMotivoData: NewMotivo;
  setNewMotivo: (motivo: NewMotivo) => void;
  setEditMotivoData: (motivo: NewMotivo) => void;
  onCreateMotivo: () => Promise<void>;
  onUpdateMotivo: (id: number) => Promise<void>;
  onDeleteMotivo: (id: number) => Promise<void>;
  onStartEditing: (motivo: Motivo) => void;
  onCancelEditing: () => void;
}

export const MotivoSection: React.FC<MotivoSectionProps> = ({
  motivos,
  isLoadingMotivos,
  isSavingMotivo,
  editingMotivo,
  newMotivo,
  editMotivoData,
  setNewMotivo,
  setEditMotivoData,
  onCreateMotivo,
  onUpdateMotivo,
  onDeleteMotivo,
  onStartEditing,
  onCancelEditing,
}) => {
  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Motivo e Responsável</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Gerencie os motivos e responsáveis disponíveis no sistema.
        </p>

        {/* Form de Adição */}
        <div className="flex gap-4 mb-8 items-end bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1 uppercase">Motivo</label>
            <input
              type="text"
              value={newMotivo.motivo}
              onChange={(e) => setNewMotivo({ ...newMotivo, motivo: e.target.value })}
              placeholder="Ex: Falta de Etiqueta"
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1 uppercase">Responsável</label>
            <input
              type="text"
              value={newMotivo.responsavel}
              onChange={(e) => setNewMotivo({ ...newMotivo, responsavel: e.target.value })}
              placeholder="Ex: João da Silva"
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <PermissionGate tela="CONFIGURACOES_VIEW" acao="criar">
            <button
              onClick={onCreateMotivo}
              disabled={isSavingMotivo}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Plus size={18} />
              Adicionar
            </button>
          </PermissionGate>
        </div>

        {/* Tabela */}
        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 font-medium border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-4 py-3">Motivo</th>
                <th className="px-4 py-3">Responsável</th>
                <th className="px-4 py-3 w-24 text-center">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {isLoadingMotivos ? (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                    <Loader2 className="animate-spin mx-auto mb-2" />
                    Carregando...
                  </td>
                </tr>
              ) : motivos.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                    Nenhum registro encontrado.
                  </td>
                </tr>
              ) : (
                motivos.map((item) => (
                  <tr key={item.id} className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    {editingMotivo === item.id ? (
                      <>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={editMotivoData.motivo}
                            onChange={(e) => setEditMotivoData({ ...editMotivoData, motivo: e.target.value })}
                            className="w-full px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={editMotivoData.responsavel}
                            onChange={(e) => setEditMotivoData({ ...editMotivoData, responsavel: e.target.value })}
                            className="w-full px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          />
                        </td>
                        <td className="px-4 py-3 flex justify-center gap-2">
                          <PermissionGate tela="CONFIGURACOES_VIEW" acao="editar">
                            <button onClick={() => onUpdateMotivo(item.id)} className="text-green-600 hover:text-green-700" title="Salvar">
                              <CheckCircle size={18} />
                            </button>
                          </PermissionGate>
                          <button onClick={onCancelEditing} className="text-red-500 hover:text-red-600" title="Cancelar">
                            <XCircle size={18} />
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{item.motivo}</td>
                        <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{item.responsavel}</td>
                        <td className="px-4 py-3 flex justify-center gap-2">
                          <PermissionGate tela="CONFIGURACOES_VIEW" acao="editar">
                            <button onClick={() => onStartEditing(item)} className="text-blue-600 hover:text-blue-700" title="Editar">
                              <Pencil size={18} />
                            </button>
                          </PermissionGate>
                          <PermissionGate tela="CONFIGURACOES_VIEW" acao="excluir">
                            <button onClick={() => onDeleteMotivo(item.id)} className="text-red-500 hover:text-red-600" title="Excluir">
                              <Trash2 size={18} />
                            </button>
                          </PermissionGate>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MotivoSection;
